import React from "react";
import { Box, Typography, IconButton, Divider, Switch } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import KeyboardArrowUpOutlinedIcon from "@mui/icons-material/KeyboardArrowUpOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const VARIANT_MAP = {
  myProfileCollapsed: { accessPoint: "my-profile", expanded: false },
  myProfileExpanded: { accessPoint: "my-profile", expanded: true },
  customerCollapsed: { accessPoint: "customer", expanded: false },
  customerExpanded: { accessPoint: "customer", expanded: true },
};

const DEFAULT_IMPACT_STATEMENTS = [
  "Role 1, Role 2: Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
  "Role 3: Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
];

const normalizeAccessPoint = (value = "my-profile") => {
  const normalized = String(value).toLowerCase().replace(/\s+/g, "-");
  return normalized.includes("customer") ? "customer" : "my-profile";
};

const metaTextSx = {
  color: "#49454F",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "16px",
  letterSpacing: "0.4px",
};

const labelTextSx = {
  color: "#21191E",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "16px",
  letterSpacing: "0.4px",
};

const DetailRow = ({ label, value, labelStyle, valueStyle }) => (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      gap: "4px",
      alignItems: "baseline",
    }}
  >
    <Typography component="span" sx={{ ...labelTextSx, ...labelStyle }}>
      {label}
    </Typography>
    <Typography component="span" sx={{ ...metaTextSx, ...valueStyle }}>
      {value}
    </Typography>
  </Box>
);

const PastProjectCard = ({
  variant = "myProfileExpanded",
  accessPoint,
  expanded,
  internalUser = false,
  showAlert = false,
  alertMessage,
  title = "Project Name",
  company = "Company/Division",
  duration = "Jan 2024 - Mar 2024",
  roles = ["Business Development Lead", "Capture Manager", "Proposal Manager"],
  linkedEmployment = "[Default Value]",
  pscCode = "[Default Value]",
  endCustomer = "[Default Value]",
  impactStatement = DEFAULT_IMPACT_STATEMENTS,
  selected,
  onSelectedChange,
  onDelete,
  onEdit,
  onToggleExpand,
  node,
  sx,
  style,
  ...props
}) => {
  const resolvedVariant =
    VARIANT_MAP[variant] || VARIANT_MAP.myProfileCollapsed;
  const resolvedAccessPoint = normalizeAccessPoint(
    accessPoint ?? resolvedVariant.accessPoint,
  );
  const isCustomer = resolvedAccessPoint === "customer";
  const [isExpandedInternal, setIsExpandedInternal] = React.useState(
    resolvedVariant.expanded,
  );
  const [isSelectedInternal, setIsSelectedInternal] = React.useState(false);

  const isExpanded = expanded ?? isExpandedInternal;
  const isSelected = selected ?? isSelectedInternal;
  const rolesText = Array.isArray(roles) ? roles.join(", ") : roles;
  const impactItems = Array.isArray(impactStatement)
    ? impactStatement
    : [impactStatement].filter(Boolean);

  const handleExpand = () => {
    const next = !isExpanded;
    if (expanded === undefined) {
      setIsExpandedInternal(next);
    }
    onToggleExpand?.(next);
  };

  const handleSelectChange = (event) => {
    const next = event.target.checked;
    if (selected === undefined) {
      setIsSelectedInternal(next);
    }
    onSelectedChange?.(next);
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        padding: "0 16px 16px 16px",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "24px",
        borderRadius: "6px",
        border: "1px solid var(--SMA-sys-light-outline-variant, #D6C1CB)",
        background: "var(--SMA-white, #FFF)",
        boxShadow:
          "0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 1px 3px 1px rgba(0, 0, 0, 0.15)",
        ...node?.style,
        height: "fit-content",
      }}
      {...props}
    >
      <Box
        sx={{
          display: "flex",
          padding: "8px 0",
          justifyContent: "space-between",
          alignItems: "flex-start",
          alignSelf: "stretch",
          width: "100%",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {internalUser && (
            <Box
              sx={{
                display: "inline-flex",
                padding: "2px 8px",
                mb: 0.75,
                borderRadius: "999px",
                backgroundColor: "#F4ECF3",
              }}
            >
              <Typography
                sx={{
                  color: "#601350",
                  fontSize: "11px",
                  fontWeight: 500,
                  lineHeight: "16px",
                }}
              >
                Internal user
              </Typography>
            </Box>
          )}

          <Typography
            sx={{
              color: "#21191E",
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "20px",
              letterSpacing: "0.1px",
              ...node?.lbl1?.style,
            }}
          >
            {node?.lbl1?.label || title}
          </Typography>

          <Typography sx={{ ...metaTextSx, ...node?.lbl2?.style }}>{node?.lbl2?.label || company}</Typography>

          <Box
            sx={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
              flexWrap: "wrap",
              mt: 0.5,
            }}
          >
            <CalendarTodayOutlinedIcon
              sx={{ fontSize: 12, color: "#49454F" }}
            />
            <Typography sx={{ ...metaTextSx, ...node?.lbl3?.style }}>{node?.lbl3?.label || duration}</Typography>
            <Typography sx={{ ...metaTextSx, ...node?.str1?.style }}> {node?.str1?.label || "•"}</Typography>
            <Typography sx={{ ...metaTextSx, minWidth: 0, flex: 1, ...node?.lbl4?.style }} noWrap>
              {node?.lbl4?.label || rolesText}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          {isCustomer ? (
            <Switch
              size="small"
              checked={isSelected}
              onChange={handleSelectChange}
              inputProps={{ "aria-label": "toggle project selection" }}
              sx={{
                mr: 0.25,
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#601350",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#601350",
                },
              }}
            />
          ) : (
            <>
              <IconButton size="small" aria-label="delete" onClick={onDelete}>
                <DeleteOutlineIcon sx={{ fontSize: 20, color: "#49454F" }} />
              </IconButton>
              <IconButton size="small" aria-label="edit" onClick={onEdit}>
                <EditOutlinedIcon sx={{ fontSize: 20, color: "#49454F" }} />
              </IconButton>
            </>
          )}

          <IconButton
            size="small"
            aria-label={isExpanded ? "collapse details" : "expand details"}
            onClick={handleExpand}
          >
            {isExpanded ? (
              <KeyboardArrowUpOutlinedIcon
                sx={{ fontSize: 20, color: "#49454F" }}
              />
            ) : (
              <KeyboardArrowDownOutlinedIcon
                sx={{ fontSize: 20, color: "#49454F" }}
              />
            )}
          </IconButton>
        </Box>
      </Box>

      {showAlert && (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "flex-start",
            alignSelf: "stretch",
            px: 1,
            py: 0.75,
            borderRadius: "4px",
            backgroundColor: "#FFF4E5",
          }}
        >
          <InfoOutlinedIcon
            sx={{ fontSize: 16, mt: "2px", color: "#8A4B00" }}
          />
          <Typography sx={{ ...metaTextSx, color: "#8A4B00" }}>
            {alertMessage ||
              (internalUser
                ? "This past project is currently visible for internal users only."
                : "Customer view is read-only until the project is added to your profile.")}
          </Typography>
        </Box>
      )}

      {isExpanded && (
        <>
          <Box
            sx={{
              display: "flex",
              padding: "12px 12px 16px 12px",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flex-start",
              gap: "8px",
              alignSelf: "stretch",
              borderRadius: "4px",
              background:
                "var(--SMA-sys-light-neutral-black-background, #F8F8F8)",
            }}
          >
            <DetailRow label={node?.str2?.label || "Linked Employment:"} value={node?.lbl5?.label || linkedEmployment} labelStyle={node?.str2?.style} valueStyle={node?.lbl5?.style} />
         
            <DetailRow label={node?.str3?.label || "PSC Code:"} value={node?.lbl6?.label || pscCode} labelStyle={node?.str3?.style} valueStyle={node?.lbl6?.style} />
            <DetailRow label={node?.str4?.label || "End Customer:"} value={node?.lbl7?.label || endCustomer} labelStyle={node?.str4?.style} valueStyle={node?.lbl7?.style} />

            <Box>
              <Typography  component="span" sx={{ ...labelTextSx, ...(node?.str5?.style || {}) }}>
                {node?.str5?.label || "Impact Statement:"}
              </Typography>
              <Box
                component="ul"
                sx={{
                  mb: 0,
                  mt: 0.5,
                  pl: 2.25,
                  color: "#49454F",
                }}
              >
                {Array(2)
                  .fill("")
                  .map((item, index) => (
                    <Typography
                      component="li"
                      sx={{
                        color: "var(--SMA-sys-light-on-surface, #21191E)",
                        fontFamily: "Roboto",
                        fontSize: "14px",

                        fontWeight: 400,
                        lineHeight: "20px",
                        letterSpacing: "0.25px",
                        textDecorationStyle: "solid",
                        textDecorationSkipInk: "auto",
                        textDecorationThickness: "auto",
                        textUnderlineOffset: "auto",
                        textUnderlinePosition: "from-font",
                        ...node?.lbl8?.style,
                      }}
                    >
                      <span
                        style={{
                          color: "var(--SMA-sys-light-on-surface, #21191E)",
                          fontFamily: "Roboto",
                          fontSize: "14px",
                          fontStyle: "italic",
                          fontWeight: 400,
                          lineHeight: "20px",
                          letterSpacing: "0.25px",
                          textDecorationLine: "underline",
                          textDecorationStyle: "solid",
                          textDecorationSkipInk: "auto",
                          textDecorationThickness: "auto",
                          textUnderlineOffset: "auto",
                          textUnderlinePosition: "from-font",
                          
                        }}
                      >
                        {" "}
                        Role {index + 1}:
                      </span>{" "}
                      {
                        "orem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitat"
                      }
                    </Typography>
                  ))}
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PastProjectCard;
